import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { PageLoadingState } from 'Components/PageLoadingState/PageLoadingState'

import { useStore } from 'Stores/index'

import { AVAILABLE_LANGUAGES, isSupportedLanguage } from 'Constants/locale'

import { GLOBAL_ROUTE_PATHS } from 'Portal/src/router/Router.constants'

import {
  EVERPHONE_HOME_URL,
  MOCK_TRACKING_NUMBER,
} from './PublicRequestReturnLabel.constants'
import {
  type PublicRequestReturnLabelFormValues,
  PublicRequestReturnLabelStateEnum,
} from './PublicRequestReturnLabel.types'
import { validateHash } from './PublicRequestReturnLabel.utils'
import { PublicRequestReturnLabelForm } from './PublicRequestReturnLabelForm'
import { PublicRequestReturnLabelHeader } from './PublicRequestReturnLabelHeader'
import { PublicRequestReturnLabelSuccess } from './PublicRequestReturnLabelSuccess'

export const PublicRequestReturnLabel = () => {
  const navigate = useNavigate()
  const { hash } = useParams<{ hash?: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const { portalSettingsStore } = useStore()
  const [pageState, setPageState] = useState<PublicRequestReturnLabelStateEnum>(
    hash
      ? PublicRequestReturnLabelStateEnum.LOADING
      : PublicRequestReturnLabelStateEnum.FORM,
  )
  const [hashError, setHashError] = useState(false)

  useEffect(() => {
    const lang = searchParams.get('lang')

    if (!lang || !isSupportedLanguage(lang)) {
      return
    }

    if (portalSettingsStore.i18n.language === lang) {
      setSearchParams(
        prev => {
          prev.delete('lang')

          return prev
        },
        { replace: true },
      )

      return
    }

    portalSettingsStore.setI18n(AVAILABLE_LANGUAGES[lang])
  }, [
    searchParams,
    setSearchParams,
    portalSettingsStore,
    portalSettingsStore.i18n.language,
  ])

  useEffect(() => {
    if (!hash) {
      return
    }

    validateHash(hash)
      .then(isValid => {
        setHashError(!isValid)
      })
      .catch(() => {
        setHashError(true)
      })
      .finally(() => {
        setPageState(PublicRequestReturnLabelStateEnum.FORM)
      })
  }, [hash])

  const handleHashRemove = () => {
    setHashError(false)
    navigate(GLOBAL_ROUTE_PATHS.public.requestReturnLabel, { replace: true })
  }

  const handleSubmit = (data: PublicRequestReturnLabelFormValues) => {
    const payload = {
      ...data,
      devices: data.devices.filter(d => d.value.trim()),
      ...(hash ? { hash } : {}),
    }

    // TODO: replace with real API call to submit return label request
    // eslint-disable-next-line no-console
    console.log('Form submitted:', payload)
    setPageState(PublicRequestReturnLabelStateEnum.SUCCESS)
  }

  const handleBackToHome = () => {
    window.location.href = EVERPHONE_HOME_URL
  }

  return (
    <div className="PublicRequestReturnLabel">
      <PublicRequestReturnLabelHeader />

      {pageState === PublicRequestReturnLabelStateEnum.SUCCESS ? (
        <div className="PublicRequestReturnLabel-card">
          {/* TODO: use real tracking number from API response */}
          <PublicRequestReturnLabelSuccess
            onBackToHome={handleBackToHome}
            trackingNumber={MOCK_TRACKING_NUMBER}
          />
        </div>
      ) : (
        <div className="PublicRequestReturnLabel-card">
          <div className="PublicRequestReturnLabel-cardInner">
            <div className="PublicRequestReturnLabel-header">
              <h1 className="PublicRequestReturnLabel-title">
                <FormattedMessage id="PublicRequestReturnLabel_title" />
              </h1>
              <p className="PublicRequestReturnLabel-description">
                <FormattedMessage id="PublicRequestReturnLabel_description" />
              </p>
            </div>

            {pageState === PublicRequestReturnLabelStateEnum.LOADING && (
              <div className="PublicRequestReturnLabel-loading">
                <PageLoadingState />
              </div>
            )}

            {pageState === PublicRequestReturnLabelStateEnum.FORM && (
              <PublicRequestReturnLabelForm
                hash={hash}
                hashError={hashError}
                onHashRemove={handleHashRemove}
                onSubmit={handleSubmit}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
